<template>
  <div class="v-idle">{{ display }}</div>
</template>

<script>
import { defineComponent, ref, onMounted, nextTick, onBeforeUnmount } from 'vue-demi'

export default defineComponent({
  name: 'VIdleModule',
  emits: ['idle', 'remind', 'refresh'],
  props: {
    duration: {
      type: Number,
      // default 5 minutes
      default: 60 * 5
    },
    events: {
      type: Array,
      default: function () { return ['mousemove', 'keypress'] }
    },
    loop: {
      type: Boolean,
      default: false
    },
    syncKey: {
      type: String,
      default: ''
    },
    reminders: {
      type: Array,
      // array of seconds
      // emit "remind" event on each second
      default: function () { return [] }
    },
    wait: {
      type: Number,
      default: 0
    }
  },
  setup (props, { emit }) {
    const display = ref('')
    const timer = ref(undefined)
    const start = ref(0)
    const counter = ref(undefined)
    const diff = ref(0)
    const minutes = ref('')
    const seconds = ref('')
    const broadcastChannel = ref(undefined)

    const isSyncEnabled = props.syncKey.length > 0 &&
      typeof window !== 'undefined' &&
      'BroadcastChannel' in window

    const shouldRemind = () => {
      if (props.reminders.length > 0) {
        if (props.reminders.includes(diff.value)) {
          remind()
        }
      }
    }

    const setDisplay = () => {
      // seconds since start
      diff.value = props.duration - (((Date.now() - start.value) / 1000) | 0)
      if (diff.value < 0 && !props.loop) {
        clearInterval(timer.value)
        clearInterval(counter.value)
        return
      }
      shouldRemind()
      // bitwise OR to handle parseInt
      const minute = (diff.value / 60) | 0
      const second = (diff.value % 60) | 0
      minutes.value = `${minute < 10 ? '0' + minute : minute}`
      seconds.value = `${second < 10 ? '0' + second : second}`
      display.value = `${minutes.value}:${seconds.value}`
    }

    const countdown = () => {
      setDisplay()
      if (diff.value <= 0 && props.loop) {
        // add second to start at the full duration
        // for instance 05:00, not 04:59
        start.value = Date.now() + 1000
      }
    }

    const idle = () => {
      emit('idle')
    }

    const remind = () => {
      emit('remind')
    }

    const setTimer = () => {
      timer.value = window.setInterval(idle, props.duration * 1000)
      counter.value = window.setInterval(countdown, 1000)
    }

    const clearEvent = (event) => {
      const clearEvent = {
        type: event.type,
        key: event instanceof KeyboardEvent ? event.key : undefined
      }
      clearTimer(clearEvent)
      // clearEvent is called only in original tab when sync is on
      if (isSyncEnabled) {
        sendBroadcastEvent(clearEvent)
      }
    }

    const clearTimer = (clearEvent) => {
      emit('refresh', clearEvent)
      clearInterval(timer.value)
      clearInterval(counter.value)
      setDisplay()
      start.value = Date.now()
      diff.value = 0
      setTimer()
    }

    const sendBroadcastEvent = (event) => {
      if (broadcastChannel.value !== undefined) {
        broadcastChannel.value.postMessage(event)
      }
    }

    const setBroadcastChannel = () => {
      broadcastChannel.value = new BroadcastChannel(props.syncKey)
      broadcastChannel.value.addEventListener('message', (event) => {
        clearTimer(event.data)
      })
    }

    onMounted(() => {
      if (isSyncEnabled) {
        setBroadcastChannel()
      }
      setTimeout(() => {
        start.value = Date.now()
        setDisplay()
        nextTick(() => {
          setTimer()
          for (let i = props.events.length - 1; i >= 0; i -= 1) {
            window.addEventListener(props.events[i], clearEvent)
          }
        })
      }, props.wait * 1000)
    })

    onBeforeUnmount(() => {
      clearInterval(timer.value)
      clearInterval(counter.value)
      for (let i = props.events.length - 1; i >= 0; i -= 1) {
        window.removeEventListener(props.events[i], clearEvent)
      }
      if (broadcastChannel.value) {
        broadcastChannel.value.close()
      }
    })

    return {
      display
    }
  }
})
</script>

<style scoped>
.v-idle {
  /* optional styles */
}
</style>
